// Conditional question logic, shared by the builder, the published form, the
// preview, the template preview and the server.
//
// There are two copies with identical logic:
//   src/utils/formLogic.js              (ES module, used by React)
//   src/backend_routes/lib/formLogic.js (CommonJS, used by Express)
// Edit this file, then run `node scripts/sync-formLogic.js` to regenerate the
// CommonJS copy. Both are covered by the same test cases (formLogic.cases.js).
//
// Conditions point at field_uid / option_uid, never at database ids: every save
// inserts new dform_fields and dfield_options rows, so ids change, uids do not.
//
// show_if shape (null = always show):
//   { match: "all" | "any", rules: [ { field: <field_uid>, op: <operator key>, value: <option_uid | number | text> } ] }

// Blocks that show content but collect no answer. They cannot be a condition
// source, but they can have a condition of their own.
export const DISPLAY_TYPES = [
    "Heading", "Start_pg_Heading", "Banner", "Divider", "Image", "Video",
    "YouTubeVideo", "PDF", "ThankYou", "Next", "Submit",
];

export const OPERATOR_LABELS = {
    is: "is",
    is_not: "is not",
    includes: "includes",
    excludes: "does not include",
    checked: "is ticked",
    unchecked: "is not ticked",
    on: "is on",
    off: "is off",
    eq: "equals",
    gt: "is greater than",
    lt: "is less than",
    text_is: "is",
    contains: "contains",
    answered: "is answered",
    empty: "is empty",
};

const SINGLE_CHOICE_TYPES = ["Dropdown", "Multiple Choice", "Picture"];
const MULTI_CHOICE_TYPES = ["Multiple Select Checkboxes", "Multiple Select"];
const NUMBER_TYPES = ["Number", "Star Rating", "Opinion Scale", "Slider"];
const SHORT_TEXT_TYPES = ["Short Answer", "text", "Email"];
const LONG_TEXT_TYPES = ["Long Answer", "Paragraph"];

// Which kind of value an operator compares against.
const OPTION_OPS = ["is", "is_not", "includes", "excludes"];
const NUMBER_OPS = ["eq", "gt", "lt"];
const TEXT_OPS = ["text_is", "contains"];

export function isDisplayType(type) {
    return DISPLAY_TYPES.includes(type);
}

export function isSourceType(type) {
    return !!type && !isDisplayType(type);
}

export function operatorsForType(type) {
    if (!isSourceType(type)) return [];
    if (SINGLE_CHOICE_TYPES.includes(type)) return ["is", "is_not", "answered", "empty"];
    if (MULTI_CHOICE_TYPES.includes(type)) return ["includes", "excludes", "answered", "empty"];
    if (type === "Checkbox") return ["checked", "unchecked"];
    if (type === "Switch") return ["on", "off"];
    if (NUMBER_TYPES.includes(type)) return ["eq", "gt", "lt", "answered", "empty"];
    if (SHORT_TEXT_TYPES.includes(type)) return ["text_is", "contains", "answered", "empty"];
    if (LONG_TEXT_TYPES.includes(type)) return ["contains", "answered", "empty"];
    return ["answered", "empty"];
}

// "option" | "number" | "text" | null (operator takes no value)
export function valueKindForOp(op) {
    if (OPTION_OPS.includes(op)) return "option";
    if (NUMBER_OPS.includes(op)) return "number";
    if (TEXT_OPS.includes(op)) return "text";
    return null;
}

// The operator a new follow-up starts with, for "Add follow-up".
export function defaultFollowUpOp(type) {
    if (SINGLE_CHOICE_TYPES.includes(type)) return "is";
    if (MULTI_CHOICE_TYPES.includes(type)) return "includes";
    if (type === "Checkbox") return "checked";
    if (type === "Switch") return "on";
    return null;
}

export function optionUid(opt) {
    return opt && typeof opt === "object" ? opt.option_uid || null : null;
}

export function optionText(opt) {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") return opt.option_text || opt.label || "";
    return String(opt);
}

// show_if arrives as a JSON string from MySQL and as an object in the browser.
// Anything that is not a rule set becomes null (always show).
export function parseShowIf(raw) {
    let value = raw;
    if (typeof value === "string") {
        if (!value.trim()) return null;
        try {
            value = JSON.parse(value);
        } catch (e) {
            return null;
        }
    }
    if (!value || typeof value !== "object" || !Array.isArray(value.rules)) return null;
    return {
        match: value.match === "any" ? "any" : "all",
        rules: value.rules
            .filter(r => r && typeof r === "object")
            .map(r => ({ field: r.field || "", op: r.op || "", value: r.value === undefined ? null : r.value })),
    };
}

export function hasCondition(field) {
    const showIf = parseShowIf(field && field.show_if);
    return !!showIf;
}

function pageRank(page) {
    // "start" is always first and "end" always last, whatever sort_order says.
    if (String(page.page_id) === "start") return -Infinity;
    if (String(page.page_id) === "end") return Infinity;
    const n = Number(page.sort_order);
    return Number.isFinite(n) ? n : 0;
}

// Pages in form order. Never use page_id or page_number arithmetic for order.
export function orderPages(pages) {
    return (pages || [])
        .map((p, i) => ({ p, i }))
        .sort((a, b) => {
            const ra = pageRank(a.p);
            const rb = pageRank(b.p);
            if (ra !== rb) return ra < rb ? -1 : 1;
            return a.i - b.i;
        })
        .map(x => x.p);
}

// ---- Page navigation (live form, Preview, template preview) ---------------
// A page is identified only by its page_id and ordered only by sort_order
// ("start" first, "end" last). page_number is never used to find a page.

// page_ids in form order, without "end".
function fillOrder(pages) {
    return orderPages(pages).map(p => String(p.page_id)).filter(id => id !== "end");
}

// Is this page_id one of the form's pages ("start" and "end" always are)?
export function hasPage(pages, pageId) {
    const id = String(pageId);
    return id === "start" || id === "end" || (pages || []).some(p => String(p.page_id) === id);
}

// The page after this one, or "end" after the last page (or an unknown page).
export function nextPageId(pages, pageId) {
    const order = fillOrder(pages);
    const i = order.indexOf(String(pageId));
    return i !== -1 && i < order.length - 1 ? order[i + 1] : "end";
}

// The page before this one, or "start".
export function previousPageId(pages, pageId) {
    const order = fillOrder(pages);
    const id = String(pageId);
    if (id === "end") return order.length ? order[order.length - 1] : "start";
    const i = order.indexOf(id);
    return i > 0 ? order[i - 1] : "start";
}

// The first page of the form (no "Previous" button there).
export function isFirstPage(pages, pageId) {
    const order = fillOrder(pages);
    return String(pageId) === "start" || (order.length > 0 && order[0] === String(pageId));
}

// Progress bar: 0 on "start", 100 on "end", evenly spread in between.
export function progressPercent(pages, pageId) {
    const id = String(pageId);
    if (id === "start") return 0;
    if (id === "end") return 100;
    const middle = fillOrder(pages).filter(p => p !== "start");
    const i = middle.indexOf(id);
    return i === -1 ? 0 : ((i + 1) / (middle.length + 1)) * 100;
}

// Fields in page sort_order, then field sort_order, then array order.
// Fields on a page that is not in `pages` go last.
export function orderFields(pages, fields) {
    const pageIndex = {};
    orderPages(pages).forEach((p, i) => { pageIndex[String(p.page_id)] = i; });
    return (fields || [])
        .map((f, i) => ({ f, i }))
        .sort((a, b) => {
            const pa = pageIndex[String(a.f.page_id)];
            const pb = pageIndex[String(b.f.page_id)];
            const na = pa === undefined ? Number.MAX_SAFE_INTEGER : pa;
            const nb = pb === undefined ? Number.MAX_SAFE_INTEGER : pb;
            if (na !== nb) return na - nb;
            const sa = Number(a.f.sort_order ?? a.f.sortOrder) || 0;
            const sb = Number(b.f.sort_order ?? b.f.sortOrder) || 0;
            if (sa !== sb) return sa - sb;
            return a.i - b.i;
        })
        .map(x => x.f);
}

function isBlank(value) {
    if (value === undefined || value === null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (typeof value === "number") return Number.isNaN(value);
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") {
        return Object.keys(value).every(k => isBlank(value[k]));
    }
    return false;
}

// Is the answer blank for the purpose of "answered" / "empty" conditions.
// Star Rating 0 means the stars were cleared.
export function isAnswerBlank(type, value) {
    if (type === "Star Rating" && Number(value) === 0) return true;
    if (type === "Checkbox" || type === "Switch") return !isTicked(value);
    return isBlank(value);
}

export function isTicked(value) {
    return value === true || value === "true";
}

function sourceOptionText(sourceField, uid) {
    const opt = (sourceField.options || []).find(o => optionUid(o) === uid);
    return opt === undefined ? null : optionText(opt);
}

function asArray(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) { /* not a JSON array */ }
    }
    if (isBlank(value)) return [];
    return [value];
}

// Does one rule match the source field's current answer?
// Choice answers are stored as option TEXT, so the rule's option_uid is mapped
// to the source field's current option text before comparing.
export function ruleMatches(rule, sourceField, answerValue) {
    if (!rule || !sourceField) return false;
    const type = sourceField.type;
    if (!operatorsForType(type).includes(rule.op)) return false;

    switch (rule.op) {
        case "answered":
            return !isAnswerBlank(type, answerValue);
        case "empty":
            return isAnswerBlank(type, answerValue);
        case "checked":
        case "on":
            return isTicked(answerValue);
        case "unchecked":
        case "off":
            return !isTicked(answerValue);
        case "is":
        case "is_not": {
            const text = sourceOptionText(sourceField, rule.value);
            if (text === null || isBlank(answerValue)) return false;
            const same = String(answerValue) === text;
            return rule.op === "is" ? same : !same;
        }
        case "includes":
        case "excludes": {
            const text = sourceOptionText(sourceField, rule.value);
            const list = asArray(answerValue).map(String);
            if (text === null || list.length === 0) return false;
            const has = list.includes(text);
            return rule.op === "includes" ? has : !has;
        }
        case "eq":
        case "gt":
        case "lt": {
            if (isAnswerBlank(type, answerValue)) return false;
            const a = Number(answerValue);
            const b = Number(rule.value);
            if (!Number.isFinite(a) || rule.value === null || rule.value === "" || !Number.isFinite(b)) return false;
            if (rule.op === "eq") return a === b;
            if (rule.op === "gt") return a > b;
            return a < b;
        }
        case "text_is":
        case "contains": {
            if (isBlank(answerValue) || rule.value === null || String(rule.value).trim() === "") return false;
            const a = String(answerValue).trim().toLowerCase();
            const b = String(rule.value).trim().toLowerCase();
            return rule.op === "text_is" ? a === b : a.includes(b);
        }
        default:
            return false;
    }
}

// { [field_uid]: true | false } for every field that has a uid.
// Walks fields in form order. A rule whose source is missing, hidden, or not
// earlier in the order is false, so hiding cascades down a chain and loops are
// impossible. `answers` is { [field_uid]: value }.
export function computeVisibility(pages, fields, answers) {
    const ordered = orderFields(pages, fields);
    const position = {};
    const byUid = {};
    ordered.forEach((f, i) => {
        if (f.field_uid) {
            position[f.field_uid] = i;
            byUid[f.field_uid] = f;
        }
    });

    const visible = {};
    const values = answers || {};
    ordered.forEach((field, index) => {
        if (!field.field_uid) return;
        const showIf = parseShowIf(field.show_if);
        if (!showIf || showIf.rules.length === 0) {
            visible[field.field_uid] = true;
            return;
        }
        const results = showIf.rules.map(rule => {
            const source = byUid[rule.field];
            if (!source) return false;
            if (!(position[rule.field] < index)) return false;
            if (visible[rule.field] !== true) return false;
            return ruleMatches(rule, source, values[rule.field]);
        });
        visible[field.field_uid] = showIf.match === "any" ? results.some(Boolean) : results.every(Boolean);
    });
    return visible;
}

export function isFieldVisible(visibility, field) {
    if (!field || !field.field_uid) return true;
    return visibility[field.field_uid] !== false;
}

function fieldName(field) {
    const label = field && typeof field.label === "string" ? field.label.trim() : "";
    return label || (field && field.type) || "Untitled question";
}

function pageName(pages, pageId) {
    const page = (pages || []).find(p => String(p.page_id) === String(pageId));
    if (page && page.page_title) return page.page_title;
    if (String(pageId) === "start") return "Start";
    if (String(pageId) === "end") return "End";
    return `Page ${pageId}`;
}

// Every logic problem in the form, in form order. Each problem:
// { field_uid, page_id, source_uid, code, message }
export function listLogicProblems(pages, fields) {
    const ordered = orderFields(pages, fields);
    const position = {};
    const byUid = {};
    ordered.forEach((f, i) => {
        if (f.field_uid) {
            position[f.field_uid] = i;
            byUid[f.field_uid] = f;
        }
    });

    const problems = [];
    ordered.forEach((field, index) => {
        const showIf = parseShowIf(field.show_if);
        if (!showIf) return;
        const prefix = `Logic on "${fieldName(field)}" (${pageName(pages, field.page_id)}): `;
        const add = (code, sourceUid, text) => problems.push({
            field_uid: field.field_uid || null,
            page_id: field.page_id,
            source_uid: sourceUid || null,
            code,
            message: prefix + text,
        });

        if (showIf.rules.length === 0) {
            add("no_rules", null, `"Show only if" has no conditions. Add a condition or choose "Always show".`);
            return;
        }

        showIf.rules.forEach(rule => {
            const source = byUid[rule.field];
            if (!source) {
                add("source_missing", rule.field, "A condition points to a question that was deleted.");
                return;
            }
            const sourceName = fieldName(source);
            if (!(position[rule.field] < index)) {
                add("source_not_earlier", rule.field,
                    `A condition uses "${sourceName}", which comes after this question. Conditions can only use questions above.`);
                return;
            }
            if (!isSourceType(source.type)) {
                add("source_not_question", rule.field, `"${sourceName}" does not collect an answer, so it cannot be used in a condition.`);
                return;
            }
            if (!operatorsForType(source.type).includes(rule.op)) {
                add("bad_operator", rule.field,
                    `The condition on "${sourceName}" uses an operator that does not fit a ${source.type} question.`);
                return;
            }
            const kind = valueKindForOp(rule.op);
            if (kind === "option") {
                if (sourceOptionText(source, rule.value) === null) {
                    add("option_missing", rule.field, `A condition points to an option of "${sourceName}" that was removed.`);
                }
            } else if (kind === "number") {
                if (rule.value === null || rule.value === "" || !Number.isFinite(Number(rule.value))) {
                    add("value_missing", rule.field, `The condition on "${sourceName}" needs a number.`);
                }
            } else if (kind === "text") {
                if (rule.value === null || String(rule.value).trim() === "") {
                    add("value_missing", rule.field, `The condition on "${sourceName}" needs a value.`);
                }
            }
        });
    });
    return problems;
}

// First logic problem, or null.
export function validateLogic(pages, fields) {
    const problems = listLogicProblems(pages, fields);
    return problems.length ? problems[0] : null;
}

function problemKey(p) {
    return `${p.field_uid}|${p.source_uid}|${p.code}`;
}

// Problems in `after` that did not exist in `before`.
export function newLogicProblems(before, after) {
    const seen = new Set((before || []).map(problemKey));
    return (after || []).filter(p => !seen.has(problemKey(p)));
}

// Follow-up nesting on ONE page. A field is nested under X when its show_if has
// exactly one rule, that rule's source X is on the same page, and the field
// sits inside X's block (X, then X's follow-ups, directly below X).
// Returns { [field_uid]: { parentUid, depth } }.
export function computeNesting(pageFields) {
    const nesting = {};
    let stack = [];
    (pageFields || []).forEach(field => {
        if (!field.field_uid) return;
        const showIf = parseShowIf(field.show_if);
        const candidate = showIf && showIf.rules.length === 1 ? showIf.rules[0].field : null;
        if (candidate && stack.includes(candidate)) {
            while (stack.length && stack[stack.length - 1] !== candidate) stack.pop();
            nesting[field.field_uid] = { parentUid: candidate, depth: stack.length };
            stack.push(field.field_uid);
        } else {
            nesting[field.field_uid] = { parentUid: null, depth: 0 };
            stack = [field.field_uid];
        }
    });
    return nesting;
}

// The field and all of its follow-ups (recursively) as a contiguous block,
// returned as [startIndex, endIndexExclusive] in pageFields.
export function blockRange(pageFields, fieldUid) {
    const nesting = computeNesting(pageFields);
    const start = (pageFields || []).findIndex(f => f.field_uid === fieldUid);
    if (start === -1) return [-1, -1];
    const depth = (nesting[fieldUid] || { depth: 0 }).depth;
    let end = start + 1;
    while (end < pageFields.length) {
        const n = nesting[pageFields[end].field_uid];
        if (!n || n.depth <= depth) break;
        end++;
    }
    return [start, end];
}

// Deleting `uids`: which other fields go too ("only because of it", i.e. every
// rule points at something being deleted, recursively) and which ones only
// lose the rules that point at the deleted fields.
// Returns { deleted: [uid...], affected: [uid...] } (deleted excludes `uids`).
export function collectDependents(fields, uids) {
    const gone = new Set(uids);
    const conditional = (fields || [])
        .map(f => ({ uid: f.field_uid, showIf: parseShowIf(f.show_if) }))
        .filter(x => x.uid && x.showIf && x.showIf.rules.length > 0);
    let changed = true;
    while (changed) {
        changed = false;
        for (const x of conditional) {
            if (gone.has(x.uid)) continue;
            if (x.showIf.rules.every(r => gone.has(r.field))) {
                gone.add(x.uid);
                changed = true;
            }
        }
    }
    const deleted = [...gone].filter(uid => !uids.includes(uid));
    const affected = (fields || [])
        .filter(f => f.field_uid && !gone.has(f.field_uid))
        .filter(f => {
            const showIf = parseShowIf(f.show_if);
            return !!showIf && showIf.rules.some(r => gone.has(r.field));
        })
        .map(f => f.field_uid);
    return { deleted, affected };
}

// Remove rules that point at any uid in `gone`. No rules left = always show.
export function stripRules(showIfRaw, gone) {
    const showIf = parseShowIf(showIfRaw);
    if (!showIf) return null;
    const goneSet = gone instanceof Set ? gone : new Set(gone);
    const rules = showIf.rules.filter(r => !goneSet.has(r.field));
    return rules.length ? { ...showIf, rules } : null;
}

// Page copies: rewrite rule sources with `fieldMap` (old uid -> new uid) and
// option values with `optionMap` (old option_uid -> new option_uid). A rule
// whose source is not in fieldMap is kept when keepOutside is true, otherwise
// dropped. Returns { showIf, dropped }.
export function remapShowIf(showIfRaw, fieldMap, optionMap, keepOutside) {
    const showIf = parseShowIf(showIfRaw);
    if (!showIf) return { showIf: null, dropped: 0 };
    let dropped = 0;
    const rules = [];
    showIf.rules.forEach(rule => {
        if (Object.prototype.hasOwnProperty.call(fieldMap, rule.field)) {
            const value = valueKindForOp(rule.op) === "option" && Object.prototype.hasOwnProperty.call(optionMap, rule.value)
                ? optionMap[rule.value]
                : rule.value;
            rules.push({ ...rule, field: fieldMap[rule.field], value });
        } else if (keepOutside) {
            rules.push(rule);
        } else {
            dropped++;
        }
    });
    return { showIf: rules.length ? { ...showIf, rules } : null, dropped };
}

// Plain-language condition, e.g. `"Leave type" is "Sick"`.
// `lookup(uid)` returns the source field (from any page) or undefined.
export function describeRule(rule, lookup) {
    const source = lookup(rule.field);
    const name = source ? `"${fieldName(source)}"` : "a deleted question";
    const label = OPERATOR_LABELS[rule.op] || rule.op;
    const kind = valueKindForOp(rule.op);
    if (kind === "option") {
        const text = source ? sourceOptionText(source, rule.value) : null;
        return `${name} ${label} ${text === null ? "(removed option)" : `"${text}"`}`;
    }
    if (kind === "number") return `${name} ${label} ${rule.value === null || rule.value === "" ? "?" : rule.value}`;
    if (kind === "text") return `${name} ${label} "${rule.value === null ? "" : rule.value}"`;
    return `${name} ${label}`;
}

export function describeCondition(showIfRaw, lookup) {
    const showIf = parseShowIf(showIfRaw);
    if (!showIf || showIf.rules.length === 0) return "";
    const joiner = showIf.match === "any" ? " or " : " and ";
    return showIf.rules.map(r => describeRule(r, lookup)).join(joiner);
}

// Required check, mirroring the published form's client rules exactly so that
// existing forms behave the same. `value` is the submitted answer (undefined if
// none), `defaultValue` the field's default.
// A required Checkbox / Switch is answered only when ticked / on ("false",
// stored when someone unticks it, is no answer).
// Known lax rules kept on purpose (separate bug): a Star Rating of 0 and an
// empty Multiple Select/Ranking array pass.
export function isRequiredMissing(field, value, defaultValue) {
    const type = field.type;
    if (type === "Next" || type === "Submit") return false;

    if (type === "Address") {
        const addr = value ?? defaultValue ?? {};
        const part = k => (addr && typeof addr[k] === "string" ? addr[k].trim() : "");
        return !part("address") || !part("city") || !part("state") || !part("zip");
    }

    if (type === "Choice Matrix") {
        const rows = Array.isArray(field.rows)
            ? field.rows
            : (field.matrix || []).filter(m => m.row_label !== null && m.row_label !== undefined).map(m => m.row_label);
        const answered = value && typeof value === "object" ? Object.keys(value) : [];
        return rows.some(row => !answered.some(a => a.toLowerCase() === String(row).toLowerCase()));
    }

    if (type === "Checkbox" || type === "Switch") {
        return !isTicked(value !== undefined && value !== null ? value : defaultValue);
    }

    if (type === "Multiple Select Checkboxes") {
        return !Array.isArray(value) || value.length === 0;
    }

    return (value ?? defaultValue ?? "") === "";
}

// ---------------------------------------------------------------------------
// Auto-fill from the employee profile.
//
// A question may start with a value from the respondent's employee profile
// (prefill_source), optionally locked (prefill_locked) so they can't change it.
// The server works out the values (never the browser) and overwrites locked
// answers on submit. An empty profile value, or a Dropdown value that matches
// no option, leaves the question empty and editable, even when locked.

export const PREFILL_SOURCES = [
    { key: "full_name", label: "Full name", identity: true },
    { key: "first_name", label: "First name", identity: true },
    { key: "last_name", label: "Last name", identity: true },
    { key: "emp_id", label: "Employee ID", identity: true },
    { key: "email", label: "Email", identity: true },
    { key: "department", label: "Department" },
    { key: "location", label: "Location" },
    { key: "job_position", label: "Job position" },
    { key: "manager_name", label: "Manager's name", identity: true },
    { key: "manager_email", label: "Manager's email", identity: true },
];

export const ANONYMOUS_IDENTITY_MESSAGE = "Turn off anonymous responses to fill identity from the profile";

const PREFILL_ALL = PREFILL_SOURCES.map(s => s.key);
const PREFILL_BY_TYPE = {
    "Short Answer": PREFILL_ALL,
    "Long Answer": PREFILL_ALL,
    "Paragraph": PREFILL_ALL,
    "Dropdown": PREFILL_ALL,
    "Email": ["email", "manager_email"],
};

// Sources offered for a question type ([] = no "Fill from profile").
export function prefillSourcesForType(type) {
    return PREFILL_BY_TYPE[type] || [];
}

export function isPrefillType(type) {
    return prefillSourcesForType(type).length > 0;
}

export function isIdentitySource(key) {
    return PREFILL_SOURCES.some(s => s.key === key && s.identity);
}

export function prefillSourceLabel(key) {
    const source = PREFILL_SOURCES.find(s => s.key === key);
    return source ? source.label : "";
}

// { source, locked } from a field, whatever shape the columns came in.
export function normalizePrefill(field) {
    const raw = field ? field.prefill_source : null;
    const source = typeof raw === "string" && raw.trim() ? raw.trim() : null;
    const flag = field ? field.prefill_locked : null;
    const locked = !!source && (flag === true || flag === 1 || flag === "1" || flag === "true");
    return { source, locked };
}

// Why this field's profile setting can't be saved, or null if it can.
export function prefillProblem(field, { anonymous = false } = {}) {
    const { source } = normalizePrefill(field);
    if (!source) return null;
    const name = fieldName(field);
    if (!PREFILL_SOURCES.some(s => s.key === source)) {
        return `"${name}" uses an unknown profile value.`;
    }
    if (!prefillSourcesForType(field.type).includes(source)) {
        return `"${name}" can't be filled with ${prefillSourceLabel(source)} (${field.type} question).`;
    }
    if (anonymous && isIdentitySource(source)) {
        return `${ANONYMOUS_IDENTITY_MESSAGE} ("${name}").`;
    }
    return null;
}

function squash(text) {
    return String(text ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

// The option whose text matches, ignoring case and extra spaces, or null.
export function matchOptionText(options, value) {
    const wanted = squash(value);
    if (!wanted) return null;
    const match = (options || []).find(opt => squash(optionText(opt)) === wanted);
    return match ? optionText(match) : null;
}

// The profile value this field starts with, or null (nothing to fill).
// `profile` maps each source key to a string or null.
export function prefillValue(field, profile, { anonymous = false } = {}) {
    const { source } = normalizePrefill(field);
    if (!source || !profile || !prefillSourcesForType(field.type).includes(source)) return null;
    if (anonymous && isIdentitySource(source)) return null;
    const raw = profile[source];
    const text = raw === undefined || raw === null ? "" : String(raw).trim();
    if (!text) return null;
    if (field.type === "Dropdown") return matchOptionText(field.options, text);
    return text;
}

// What the page gets for a prefilled field: { value, locked }, or null.
export function prefillFor(field, profile, options) {
    const value = prefillValue(field, profile, options);
    if (value === null) return null;
    return { value, locked: normalizePrefill(field).locked };
}

// The answer an untouched question holds when the form is submitted: the
// value it started with (profile value or default). An unticked Checkbox or
// Switch, or an empty start, is no answer. undefined = nothing to send.
export function startingAnswer(field) {
    if (!field || isDisplayType(field.type) || field.type === "Document Type" || field.type === "Signature") return undefined;
    const value = field.default_value;
    if (value === undefined || value === null) return undefined;
    if (field.type === "Checkbox" || field.type === "Switch") return isTicked(value) ? "true" : undefined;
    if (typeof value === "string" && value.trim() === "") return undefined;
    return value;
}

// ---------------------------------------------------------------------------
// Signature questions. A signature identifies the person, so anonymous forms
// can't collect one (checked when saving the form and when turning anonymous
// responses on).

export const ANONYMOUS_SIGNATURE_MESSAGE = "Turn off anonymous responses to collect signatures";

// Why this field can't be saved on this form, or null.
export function signatureProblem(field, { anonymous = false } = {}) {
    if (!anonymous || !field || field.type !== "Signature") return null;
    return `${ANONYMOUS_SIGNATURE_MESSAGE} ("${fieldName(field)}").`;
}
